import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowUpRight,
    Download,
    Copy,
    KeyRound,
    Activity,
    ShieldCheck,
    Search,
    Mail,
    CreditCard,
    CheckCircle2,
    CircleAlert,
    MonitorCheck,
    Send,
    LifeBuoy,
    MonitorX,
    RefreshCw,
} from "lucide-react";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { accountRequest, firstName } from "@/accountApi";
import { getSiteConfig } from "@/siteConfig";

function statusText(licence) {
    if (!licence) return "No licence";
    if (licence.licenseStatus === "active") return "Active";
    return licence.licenseStatus || "Unknown";
}

function friendlyDate(value) {
    const text = String(value || "").trim();
    if (!text) return "-";
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return text;
    const [, year, month, day] = match;
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(date);
}

function humanStatus(value) {
    const text = String(value || "").trim();
    if (!text) return "Manual";
    return text
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [resendingVerification, setResendingVerification] = useState(false);
    const [resendingLicenceEmail, setResendingLicenceEmail] = useState(false);
    const [linkingLicence, setLinkingLicence] = useState(false);
    const [submittingSupport, setSubmittingSupport] = useState(false);
    const [releasingDeviceId, setReleasingDeviceId] = useState("");
    const [licenceKeyToLink, setLicenceKeyToLink] = useState("");
    const [supportForm, setSupportForm] = useState({
        category: "licence",
        priority: "normal",
        subject: "",
        message: "",
    });
    const [searchParams, setSearchParams] = useSearchParams();
    const { version } = getSiteConfig();

    useEffect(() => {
        let cancelled = false;
        accountRequest("/api/account/dashboard")
            .then((payload) => {
                if (!cancelled) setData(payload);
            })
            .catch((error) => {
                if (error?.status !== 401) {
                    toast.error("Could not load dashboard", {
                        description: error instanceof Error ? error.message : "Try again later.",
                    });
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (searchParams.get("verified") !== "1") return;
        toast.success("Email verified", {
            description: "Licence access and downloads are now unlocked for active plans.",
        });
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("verified");
        setSearchParams(nextParams, { replace: true });
    }, [searchParams, setSearchParams]);

    const refreshDashboard = async () => {
        const payload = await accountRequest("/api/account/dashboard");
        setData(payload);
        return payload;
    };

    const site = data?.site || { version, downloadUrl: "", supportEmail: "support@reachforge.io" };
    const canDownload = Boolean(site.downloadUrl);
    const user = data?.user || {};
    const emailVerified = Boolean(user.emailVerified);
    const licence = data?.licence || null;
    const access = data?.access || {};
    const billing = data?.billing || null;
    const support = data?.support || {};
    const recentSupportRequests = support.recentRequests || [];
    const supportEmail = site.supportEmail || "support@reachforge.io";
    const supportHref = `mailto:${supportEmail}?subject=ReachForge%20Leads%20support`;
    const shownKey = licence?.licenseKey || (licence?.keyLast4 ? `•••• •••• •••• ${licence.keyLast4}` : "Not available yet");

    const downloadUnavailableText = loading
        ? "Loading download..."
        : !emailVerified
            ? "Verify email to download"
            : !licence
                ? "Link licence to download"
                : !licence.accessAllowed
                    ? "Active licence required"
                    : "Download unavailable";

    const copy = () => {
        if (!licence?.licenseKey) {
            toast.error("Full licence key is not available", {
                description: emailVerified
                    ? "Use the original licence email or contact support if you need it resent."
                    : "Verify your account email before viewing the full licence key.",
            });
            return;
        }
        navigator.clipboard
            ?.writeText(licence.licenseKey)
            .then(() => toast.success("Licence key copied"))
            .catch(() => toast.error("Couldn't copy — please copy manually"));
    };

    const manageBilling = async () => {
        try {
            const payload = await accountRequest("/api/account/portal", {
                method: "POST",
                body: {},
            });
            if (payload.url) window.location.href = payload.url;
        } catch (error) {
            toast.error("Billing portal unavailable", {
                description: error instanceof Error ? error.message : "Please contact support.",
            });
        }
    };

    const resendVerification = async () => {
        if (resendingVerification) return;
        setResendingVerification(true);
        try {
            await accountRequest("/api/account/resend-verification", {
                method: "POST",
                body: {},
            });
            toast.success("Verification email sent", {
                description: `Check ${user.email || "your inbox"} for the verification link.`,
            });
        } catch (error) {
            toast.error("Could not send verification email", {
                description: error instanceof Error ? error.message : "Try again shortly.",
            });
        } finally {
            setResendingVerification(false);
        }
    };

    const resendLicenceEmail = async () => {
        if (resendingLicenceEmail) return;
        setResendingLicenceEmail(true);
        try {
            const payload = await accountRequest("/api/account/resend-licence-email", {
                method: "POST",
                body: {},
            });
            toast.success("Licence email sent", {
                description: `Sent to ${payload.email || user.email || "your account email"}.`,
            });
        } catch (error) {
            toast.error("Could not resend licence email", {
                description: error instanceof Error ? error.message : "Contact support if the key is missing.",
            });
        } finally {
            setResendingLicenceEmail(false);
        }
    };

    const linkLicence = async (event) => {
        event.preventDefault();
        if (linkingLicence) return;
        setLinkingLicence(true);
        try {
            await accountRequest("/api/account/link-licence", {
                method: "POST",
                body: { license_key: licenceKeyToLink },
            });
            setLicenceKeyToLink("");
            await refreshDashboard();
            toast.success("Licence linked");
        } catch (error) {
            toast.error("Could not link licence", {
                description: error instanceof Error ? error.message : "Check the key and try again.",
            });
        } finally {
            setLinkingLicence(false);
        }
    };

    const updateSupportForm = (field) => (event) => {
        setSupportForm((current) => ({ ...current, [field]: event.target.value }));
    };

    const submitSupport = async (event) => {
        event.preventDefault();
        if (submittingSupport) return;
        setSubmittingSupport(true);
        try {
            const payload = await accountRequest("/api/account/support", {
                method: "POST",
                body: supportForm,
            });
            setSupportForm((current) => ({ ...current, subject: "", message: "" }));
            await refreshDashboard();
            toast.success("Support request sent", {
                description: payload.request?.requestId ? `Reference ${payload.request.requestId}` : "We'll reply by email.",
            });
        } catch (error) {
            toast.error("Could not send support request", {
                description: error instanceof Error ? error.message : "Try again later or email support directly.",
            });
        } finally {
            setSubmittingSupport(false);
        }
    };

    const releaseDevice = async (device) => {
        if (!device?.deviceId || releasingDeviceId) return;
        const label = device.name || "this computer";
        if (!window.confirm(`Release ${label} from this licence?`)) return;
        setReleasingDeviceId(device.deviceId);
        try {
            await accountRequest("/api/account/device/release", {
                method: "POST",
                body: { device_id: device.deviceId },
            });
            await refreshDashboard();
            toast.success("Computer released", {
                description: `${label} can be activated again later with the licence key.`,
            });
        } catch (error) {
            toast.error("Could not release computer", {
                description: error instanceof Error ? error.message : "Try again later or contact support.",
            });
        } finally {
            setReleasingDeviceId("");
        }
    };

    const stats = [
        { k: licence ? statusText(licence) : "None", v: "licence status", icon: ShieldCheck },
        { k: licence?.computerAllowance || "No plan", v: "computer allowance", icon: Activity },
        { k: licence ? `${licence.activatedDevices}/${licence.deviceLimit}` : "0/0", v: "activated computers", icon: Search },
        { k: friendlyDate(licence?.expiresAt), v: "valid until", icon: Activity },
    ];

    const renewalText = !billing
        ? "No subscription linked"
        : billing.cancelAtPeriodEnd
            ? `Ends ${friendlyDate(billing.currentPeriodEnd)}`
            : billing.trialing
                ? `Trial ends ${friendlyDate(billing.currentPeriodEnd)}`
                : billing.currentPeriodEnd
                    ? `Renews ${friendlyDate(billing.currentPeriodEnd)}`
                    : "Manual billing";

    const billingTitle = !licence
        ? "No billing record linked."
        : billing?.cancelAtPeriodEnd
            ? "Renewal cancellation scheduled."
            : billing?.trialing
                ? "Free Month trial active."
                : licence.accessAllowed
                    ? "Subscription access active."
                    : "Billing needs attention.";

    const checklist = [
        {
            label: "Email verified",
            value: emailVerified ? friendlyDate(user.emailVerifiedAt) : "Action needed",
            done: emailVerified,
            icon: Mail,
        },
        {
            label: "Licence linked",
            value: licence?.planLabel || "No licence",
            done: Boolean(access.licenceLinked),
            icon: KeyRound,
        },
        {
            label: "Plan access",
            value: licence?.accessAllowed ? statusText(licence) : "Inactive",
            done: Boolean(access.licenceActive),
            icon: ShieldCheck,
        },
        {
            label: "Installer",
            value: canDownload ? `v${site.version || version}` : downloadUnavailableText,
            done: Boolean(access.downloadAllowed),
            icon: Download,
        },
        {
            label: "Computer activation",
            value: licence ? `${licence.activatedDevices}/${licence.deviceLimit}` : "0/0",
            done: Boolean((licence?.activatedDevices || 0) > 0),
            icon: MonitorCheck,
        },
    ];

    const accountRows = [
        ["Account email", user.email || "-"],
        ["Company", user.companyName || "-"],
        ["Created", friendlyDate(user.accountCreatedAt)],
        ["Last sign in", friendlyDate(user.lastLoginAt)],
    ];

    const billingRows = [
        ["Plan", licence ? `${licence.planLabel || "Manual licence"} ${licence.amountLabel || ""}`.trim() : "-"],
        ["Subscription", billing ? humanStatus(billing.subscriptionStatus) : "-"],
        ["Renewal", renewalText],
        ["Billing portal", billing?.portalAvailable ? "Available" : emailVerified ? "Unavailable" : "Verify email"],
    ];

    const setupSteps = [
        ["Account", emailVerified ? "Verified" : "Verify email"],
        ["Licence", licence ? "Linked" : "Link key"],
        ["Installer", canDownload ? `Available v${site.version || version}` : downloadUnavailableText],
        ["Activation", licence?.activatedDevices ? `${licence.activatedDevices} computer${licence.activatedDevices === 1 ? "" : "s"} active` : "Not activated yet"],
        ["Support", recentSupportRequests.length ? `${recentSupportRequests.length} recent request${recentSupportRequests.length === 1 ? "" : "s"}` : "Ready"],
    ];

    return (
        <DashboardShell
            eyebrow="Customer area"
            title={loading ? "Loading dashboard..." : `Hello, ${firstName(user.name || user.email)}.`}
            subtitle="Manage your licence, download the latest build, and review account-level support information."
            licence={licence}
            site={site}
            actions={
                <>
                    {canDownload ? (
                        <a data-testid="dashboard-cta-download" href={site.downloadUrl} className="btn btn-accent w-full h-12">
                            <Download size={14} /> Download v{site.version}
                        </a>
                    ) : (
                        <button data-testid="dashboard-cta-download" type="button" disabled className="btn btn-accent w-full h-12 opacity-60 cursor-not-allowed">
                            <Download size={14} /> {downloadUnavailableText}
                        </button>
                    )}
                    <a href={supportHref} className="btn btn-secondary w-full h-12">
                        <Mail size={14} /> Contact support
                    </a>
                </>
            }
        >
            {!loading && !emailVerified && (
                <section data-testid="dashboard-email-verification" className="border border-ink bg-forge/15 p-6 md:p-7 mb-px">
                    <div className="grid lg:grid-cols-12 gap-5 items-center">
                        <div className="lg:col-span-8">
                            <div className="overline">Account security</div>
                            <h2 className="font-display text-2xl md:text-3xl font-black tracking-tighter text-ink mt-2">
                                Verify your email to unlock licence access.
                            </h2>
                            <p className="text-sm text-ink-soft mt-2 max-w-2xl">
                                Full licence keys, billing, and installer downloads stay locked until this account email is verified.
                            </p>
                        </div>
                        <div className="lg:col-span-4 flex lg:justify-end">
                            <button
                                type="button"
                                onClick={resendVerification}
                                disabled={resendingVerification}
                                className="btn btn-primary h-11 px-4 text-xs w-full sm:w-auto disabled:opacity-70"
                            >
                                {resendingVerification ? "Sending..." : "Send verification email"}
                                <Mail size={12} />
                            </button>
                        </div>
                    </div>
                </section>
            )}

            <div data-testid="dashboard-licence-block" className="border border-ink grid lg:grid-cols-12">
                <div className="lg:col-span-7 bg-ink text-bg p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-bg/20">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 border border-bg/40 flex items-center justify-center text-forge">
                                <KeyRound size={18} strokeWidth={2.25} />
                            </div>
                            <span className="font-mono uppercase tracking-[0.2em] text-[11px]" style={{ color: "hsl(var(--ink-mute))" }}>
                                Licence key · {licence?.planLabel || "No linked plan"}
                            </span>
                        </div>
                        <span className="bg-forge text-bg px-2 py-1 font-mono text-[10px] tracking-[0.2em] uppercase">
                            {statusText(licence)}
                        </span>
                    </div>
                    <div data-testid="dashboard-licence-key" className="mt-6 font-mono text-xl md:text-2xl tracking-[0.05em] text-bg break-all">
                        {loading ? "Loading..." : shownKey}
                    </div>
                    {!licence && !loading && (
                        <p className="mt-4 text-bg/65 text-sm">
                            No licence is linked to this account yet. Use the same email address at checkout, or contact support if your purchase used another email.
                        </p>
                    )}
                    <div className="mt-6 flex gap-2 flex-wrap">
                        <button data-testid="dashboard-copy-key" onClick={copy} className="btn btn-accent h-10 px-4 text-xs" disabled={!licence?.fullKeyAvailable}>
                            <Copy size={12} /> Copy key
                        </button>
                        <button
                            data-testid="dashboard-email-key"
                            type="button"
                            onClick={resendLicenceEmail}
                            className="btn h-10 px-4 text-xs border-bg/30 bg-ink text-bg hover:bg-bg hover:text-ink"
                            disabled={!licence?.canResendLicenceEmail || resendingLicenceEmail}
                        >
                            <Mail size={12} /> {resendingLicenceEmail ? "Sending..." : "Email key"}
                        </button>
                        <button onClick={manageBilling} className="btn h-10 px-4 text-xs border-bg/30 bg-ink text-bg hover:bg-bg hover:text-ink" disabled={!licence?.canManageBilling}>
                            Billing, invoices & renewal <ArrowUpRight size={12} />
                        </button>
                    </div>
                    <p className="mt-6 text-xs font-mono uppercase tracking-[0.2em] text-bg/50">
                        {licence
                            ? `${licence.cancelAtPeriodEnd ? "Renewal cancellation scheduled" : `Valid until ${licence.expiresAt || "unknown"}`} · ${licence.activatedDevices} activated computer(s)`
                            : "Buy a plan or register with your checkout email to link a licence."}
                    </p>
                </div>
                <div className="lg:col-span-5 grid grid-cols-2">
                    {stats.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <div
                                key={`${s.v}-${i}`}
                                data-testid={`dashboard-stat-${i + 1}`}
                                className={`p-6 md:p-8 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b" : ""} border-border`}
                            >
                                <Icon size={16} strokeWidth={2.25} className="text-ink-mute" />
                                <div className="font-display text-2xl md:text-3xl font-black tracking-tighter text-ink mt-3 leading-none break-words">
                                    {loading ? "..." : s.k}
                                </div>
                                <div className="overline mt-2">{s.v}</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <section data-testid="dashboard-link-licence" className="mt-px border border-border bg-bg p-7 md:p-8">
                <div className="grid lg:grid-cols-12 gap-6 items-end">
                    <div className="lg:col-span-5">
                        <div className="overline">Link existing licence</div>
                        <h2 className="font-display text-2xl md:text-3xl font-black tracking-tighter text-ink mt-2">
                            Bought with another email?
                        </h2>
                        <p className="text-sm text-ink-soft mt-2">
                            Verify this account email, then enter the licence key from your purchase email to link it here.
                        </p>
                    </div>
                    <form onSubmit={linkLicence} className="lg:col-span-7 grid md:grid-cols-[1fr_auto] gap-3">
                        <input
                            data-testid="dashboard-link-licence-input"
                            value={licenceKeyToLink}
                            onChange={(event) => setLicenceKeyToLink(event.target.value)}
                            type="text"
                            autoComplete="off"
                            className="input-sharp"
                            placeholder="Paste licence key"
                            disabled={!emailVerified || linkingLicence}
                        />
                        <button
                            data-testid="dashboard-link-licence-submit"
                            type="submit"
                            className="btn btn-secondary h-12 px-5 disabled:opacity-60"
                            disabled={!emailVerified || linkingLicence || !licenceKeyToLink.trim()}
                        >
                            {linkingLicence ? "Linking..." : "Link licence"}
                            <KeyRound size={13} />
                        </button>
                    </form>
                </div>
            </section>

            <div className="mt-px grid xl:grid-cols-[1.15fr_0.85fr] border border-border">
                <section data-testid="dashboard-access-summary" className="bg-bg p-7 md:p-8 border-b xl:border-b-0 xl:border-r border-border">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="overline">Account status</div>
                            <h2 className="font-display text-2xl md:text-3xl font-black tracking-tighter text-ink mt-2">
                                {emailVerified && licence?.accessAllowed ? "Ready for app access." : "Action required before full access."}
                            </h2>
                        </div>
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-mute">
                            {emailVerified ? "Verified account" : "Email pending"}
                        </span>
                    </div>
                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 border border-border">
                        {checklist.map((item, index) => {
                            const Icon = item.icon;
                            const StateIcon = item.done ? CheckCircle2 : CircleAlert;
                            return (
                                <div
                                    key={item.label}
                                    className={`p-4 md:p-5 ${index < checklist.length - 1 ? "border-b sm:border-b-0 sm:border-r" : ""} border-border`}
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <Icon size={15} strokeWidth={2.25} className="text-ink-mute" />
                                        <StateIcon size={15} strokeWidth={2.5} className={item.done ? "text-forge" : "text-ink-mute"} />
                                    </div>
                                    <div className="mt-4 text-sm font-semibold text-ink">{item.label}</div>
                                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-mute break-words">
                                        {loading ? "..." : item.value}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <dl className="mt-6 grid md:grid-cols-2 gap-x-8 gap-y-3">
                        {accountRows.map(([label, value]) => (
                            <div key={label} className="grid grid-cols-[130px_1fr] gap-3 border-b border-border pb-3">
                                <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-mute">{label}</dt>
                                <dd className="text-sm text-ink-soft break-words">{loading ? "..." : value}</dd>
                            </div>
                        ))}
                    </dl>
                </section>

                <section data-testid="dashboard-billing-summary" className="bg-surface p-7 md:p-8">
                    <div className="overline">Billing summary</div>
                    <h2 className="font-display text-2xl md:text-3xl font-black tracking-tighter text-ink mt-2">
                        {billingTitle}
                    </h2>
                    <dl className="mt-6 grid gap-4">
                        {billingRows.map(([label, value]) => (
                            <div key={label} className="grid sm:grid-cols-[140px_1fr] gap-1 sm:gap-4 border-b border-border pb-3 last:border-b-0">
                                <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-mute">{label}</dt>
                                <dd className="text-sm text-ink-soft break-words">{loading ? "..." : value}</dd>
                            </div>
                        ))}
                    </dl>
                    <button
                        type="button"
                        onClick={manageBilling}
                        className="btn btn-secondary h-11 px-4 text-xs mt-6 disabled:opacity-60"
                        disabled={!licence?.canManageBilling}
                    >
                        Open billing portal <ArrowUpRight size={12} />
                    </button>
                    <p className="mt-4 text-xs text-ink-soft">
                        Stripe manages invoices, cards, tax details, cancellation, and renewal changes.
                    </p>
                </section>
            </div>

            <div className="mt-px grid lg:grid-cols-2 border border-border">
                <section data-testid="dashboard-licence-details" className="bg-bg p-7 md:p-8 border-b lg:border-b-0 lg:border-r border-border">
                    <div className="overline">Licence details</div>
                    <dl className="mt-5 grid gap-4">
                        {[
                            ["Plan", licence ? `${licence.planLabel || "Manual licence"} ${licence.amountLabel || ""}`.trim() : "No linked licence"],
                            ["Expiry date", friendlyDate(licence?.expiresAt)],
                            ["Subscription", licence?.subscriptionStatus ? humanStatus(licence.subscriptionStatus) : "Manual / not linked"],
                            ["Billing", licence?.cancelAtPeriodEnd ? "Renewal cancellation scheduled" : "Renewal active when subscription is active"],
                            ["Download", canDownload ? `Windows installer v${site.version || version}` : downloadUnavailableText],
                        ].map(([label, value]) => (
                            <div key={label} className="grid sm:grid-cols-[150px_1fr] gap-1 sm:gap-4 border-b border-border pb-3 last:border-b-0">
                                <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-mute">{label}</dt>
                                <dd className="text-sm text-ink-soft break-words">{loading ? "..." : value}</dd>
                            </div>
                        ))}
                    </dl>
                    <div className="mt-6 flex flex-wrap gap-2">
                        {canDownload ? (
                            <a href={site.downloadUrl} className="btn btn-accent h-10 px-4 text-xs">
                                Download app <Download size={12} />
                            </a>
                        ) : (
                            <button type="button" disabled className="btn btn-accent h-10 px-4 text-xs opacity-60 cursor-not-allowed">
                                {downloadUnavailableText} <Download size={12} />
                            </button>
                        )}
                        <button onClick={manageBilling} className="btn btn-secondary h-10 px-4 text-xs" disabled={!licence?.canManageBilling}>
                            Invoices / billing <ArrowUpRight size={12} />
                        </button>
                        <button
                            type="button"
                            onClick={resendLicenceEmail}
                            className="btn btn-secondary h-10 px-4 text-xs"
                            disabled={!licence?.canResendLicenceEmail || resendingLicenceEmail}
                        >
                            {resendingLicenceEmail ? "Sending..." : "Email licence key"} <RefreshCw size={12} />
                        </button>
                    </div>
                </section>

                <section data-testid="dashboard-device-details" className="bg-bg p-7 md:p-8">
                    <div className="overline">Devices used</div>
                    <div className="mt-4 font-display text-3xl md:text-4xl font-black tracking-tighter text-ink leading-none">
                        {loading ? "..." : licence ? `${licence.activatedDevices}/${licence.deviceLimit}` : "0/0"}
                    </div>
                    <p className="mt-2 text-sm text-ink-soft">
                        {licence?.computerAllowance || "No active device allowance is linked to this account."}
                    </p>
                    <div className="mt-6 border border-border">
                        {(licence?.devices || []).length > 0 ? (
                            licence.devices.map((device, index) => (
                                <div key={`${device.name}-${device.lastSeenAt}-${index}`} className="p-4 border-b border-border last:border-b-0">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <div className="text-sm font-semibold text-ink">{device.name || "Unnamed computer"}</div>
                                            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-mute">
                                                Last seen {friendlyDate(device.lastSeenAt)}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => releaseDevice(device)}
                                            disabled={!device.deviceId || releasingDeviceId === device.deviceId}
                                            className="btn btn-secondary h-9 px-3 text-[11px] shrink-0 disabled:opacity-60"
                                        >
                                            <MonitorX size={12} />
                                            {releasingDeviceId === device.deviceId ? "Releasing..." : "Release"}
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-4 text-sm text-ink-soft">
                                No activated computers are recorded yet. The first app activation will appear here.
                            </div>
                        )}
                    </div>
                    <a href={supportHref} className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-ink link-underline">
                        Need help with devices? <ArrowUpRight size={12} className="text-forge" />
                    </a>
                </section>
            </div>

            <div className="mt-px grid md:grid-cols-3 grid-skeleton border border-border">
                <div data-testid="dashboard-action-download" className="bg-bg p-7 group hover:bg-surface transition-colors">
                    <Download size={16} strokeWidth={2.25} className="text-ink-mute" />
                    <h3 className="font-display text-lg font-semibold text-ink mt-3">Download latest installer</h3>
                    <p className="text-ink-soft text-sm mt-2">Install or update ReachForge Leads on an allowed computer for this licence.</p>
                    {canDownload ? (
                        <a href={site.downloadUrl} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-ink link-underline">
                            Download v{site.version || version} <ArrowUpRight size={12} className="text-forge" />
                        </a>
                    ) : (
                        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-ink-mute">
                            {downloadUnavailableText}
                        </span>
                    )}
                </div>
                <div data-testid="dashboard-action-billing" className="bg-bg p-7 group hover:bg-surface transition-colors">
                    <CreditCard size={16} strokeWidth={2.25} className="text-ink-mute" />
                    <h3 className="font-display text-lg font-semibold text-ink mt-3">Billing and invoices</h3>
                    <p className="text-ink-soft text-sm mt-2">Open Stripe Customer Portal to update card details, view invoices, or manage renewal.</p>
                    <button type="button" onClick={manageBilling} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-ink link-underline disabled:opacity-50 bg-transparent border-0 p-0 cursor-pointer disabled:cursor-not-allowed" disabled={!licence?.canManageBilling}>
                        Manage billing <ArrowUpRight size={12} className="text-forge" />
                    </button>
                </div>
                <div data-testid="dashboard-action-support" className="bg-bg p-7 group hover:bg-surface transition-colors">
                    <Mail size={16} strokeWidth={2.25} className="text-ink-mute" />
                    <h3 className="font-display text-lg font-semibold text-ink mt-3">Support</h3>
                    <p className="text-ink-soft text-sm mt-2">Contact support for licence, activation, payment, or setup help.</p>
                    <a href={supportHref} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-ink link-underline">
                        Email support <ArrowUpRight size={12} className="text-forge" />
                    </a>
                </div>
            </div>

            <div className="mt-px border border-border">
                <section data-testid="dashboard-setup-path" className="bg-bg p-7 md:p-8 border-b border-border">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="overline">Setup path</div>
                            <h2 className="font-display text-2xl md:text-3xl font-black tracking-tighter text-ink mt-2">
                                Account, licence, installer, activation.
                            </h2>
                        </div>
                        <LifeBuoy size={18} strokeWidth={2.25} className="text-ink-mute" />
                    </div>
                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 border border-border">
                        {setupSteps.map(([label, value], index) => (
                            <div
                                key={label}
                                className={`p-4 md:p-5 ${index < setupSteps.length - 1 ? "border-b sm:border-b-0 sm:border-r" : ""} border-border`}
                            >
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-mute">{label}</div>
                                <div className="mt-2 text-sm font-semibold text-ink break-words">{loading ? "..." : value}</div>
                            </div>
                        ))}
                    </div>
                </section>

                <section data-testid="dashboard-support-request" className="bg-surface p-7 md:p-8">
                    <div className="grid xl:grid-cols-[0.8fr_1.2fr] gap-8">
                        <div>
                            <div className="overline">Support request</div>
                            <h2 className="font-display text-2xl md:text-3xl font-black tracking-tighter text-ink mt-2">
                                Send account-linked support.
                            </h2>
                            <p className="text-sm text-ink-soft mt-3">
                                Requests include your signed-in account and linked licence reference when available.
                            </p>
                            <div className="mt-6 border border-border bg-bg">
                                {recentSupportRequests.length > 0 ? (
                                    recentSupportRequests.map((request) => (
                                        <div key={request.requestId} className="p-4 border-b border-border last:border-b-0">
                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-sm font-semibold text-ink">{request.subject}</span>
                                                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-mute">
                                                    {request.status}
                                                </span>
                                            </div>
                                            <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-mute">
                                                {request.categoryLabel} · {friendlyDate(request.createdAt)}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-4 text-sm text-ink-soft">No support requests are recorded for this account yet.</div>
                                )}
                            </div>
                            <a href={supportHref} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-ink link-underline">
                                Email support directly <ArrowUpRight size={12} className="text-forge" />
                            </a>
                        </div>

                        <form onSubmit={submitSupport} className="grid gap-4">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-mute">Category</label>
                                    <select
                                        data-testid="dashboard-support-category"
                                        value={supportForm.category}
                                        onChange={updateSupportForm("category")}
                                        className="input-sharp mt-2"
                                    >
                                        <option value="licence">Licence</option>
                                        <option value="billing">Billing</option>
                                        <option value="activation">Activation</option>
                                        <option value="setup">Setup</option>
                                        <option value="bug">Bug</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-mute">Priority</label>
                                    <select
                                        data-testid="dashboard-support-priority"
                                        value={supportForm.priority}
                                        onChange={updateSupportForm("priority")}
                                        className="input-sharp mt-2"
                                    >
                                        <option value="normal">Normal</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-mute">Subject</label>
                                <input
                                    data-testid="dashboard-support-subject"
                                    value={supportForm.subject}
                                    onChange={updateSupportForm("subject")}
                                    type="text"
                                    maxLength={160}
                                    className="input-sharp mt-2"
                                    placeholder="Short summary"
                                    required
                                />
                            </div>
                            <div>
                                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-mute">Message</label>
                                <textarea
                                    data-testid="dashboard-support-message"
                                    value={supportForm.message}
                                    onChange={updateSupportForm("message")}
                                    rows={6}
                                    maxLength={2000}
                                    className="input-sharp mt-2 min-h-[150px] py-3 resize-y"
                                    placeholder="What needs attention?"
                                    required
                                />
                            </div>
                            <button
                                data-testid="dashboard-support-submit"
                                type="submit"
                                disabled={submittingSupport || !supportForm.subject.trim() || !supportForm.message.trim()}
                                className="btn btn-primary h-12 px-5 disabled:opacity-60 justify-self-start"
                            >
                                {submittingSupport ? "Sending..." : "Send support request"}
                                <Send size={13} />
                            </button>
                        </form>
                    </div>
                </section>
            </div>
        </DashboardShell>
    );
}
